import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import { useCaseDataContext } from '../CaseDataContext';
import { useDatabase } from './useDatabase';
import { buildSeatLayout, assignJurorsToLayout } from '../../util/seatLayout';

const EMPTY_SCORECARD = {
  causeCount: 0,
  hardshipCount: 0,
  prosecutionPeremptCount: 0,
  defensePeremptCount: 0,
};

async function fetchJurorsForCase(db, caseId) {
  if (!db || !caseId) {
    return [];
  }

  return db.jurors.where('caseId').equals(caseId).toArray();
}

function filterPoolJurors(jurors, sortConfig = {}) {
  const pool = jurors.filter(
    (j) =>
      (j.seatNumber == null || j.seatNumber <= 0) &&
      (j.alternateSeatNumber == null || j.alternateSeatNumber <= 0) &&
      !j.discarded &&
      !j.alternateDiscarded,
  );

  pool.sort((a, b) => {
    if (sortConfig.field && sortConfig.direction) {
      const aVal = a[sortConfig.field] || '';
      const bVal = b[sortConfig.field] || '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'desc' ? -cmp : cmp;
    }

    return (a.created || '').localeCompare(b.created || '');
  });

  return pool;
}

function filterDismissedJurors(jurors, mode = 'jury-box') {
  const discardedField =
    mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';
  const dismissed = jurors.filter((j) => j[discardedField]);

  dismissed.sort((a, b) => (b.updated || '').localeCompare(a.updated || ''));

  return dismissed;
}

function buildRowJurors(jurors, rowNum, numCols, spacers = [], mode = 'jury-box', customSeatNumbers = {}, size = 0) {
  // Resolve jurors to spaces over the WHOLE grid, not just this row — a
  // simple number->juror map breaks when custom renumbering leaves two
  // spaces sharing a number (jurors stack in one space / vanish from the
  // other). The shared assignment claims each juror for exactly one space.
  const layout = buildSeatLayout(size, mode === 'jury-box' ? spacers : [], customSeatNumbers);
  const assignment = assignJurorsToLayout(layout, jurors, mode);

  const sortedSpacers = [...spacers].sort((a, b) => a - b);
  const result = [];
  let previousSpacers = sortedSpacers.filter((s) => s <= rowNum * numCols);

  for (let i = 0; i < numCols; i++) {
    const spaceNumber = i + 1 + rowNum * numCols;
    const isSpacer = sortedSpacers.includes(spaceNumber);

    if (isSpacer && !previousSpacers.includes(spaceNumber)) {
      previousSpacers.push(spaceNumber);
    }

    const defaultSeatNumber = spaceNumber - previousSpacers.length;
    // The user can assign a custom seat number to any space so the on-screen
    // grid can mirror the physical courtroom numbering.
    const customVal =
      customSeatNumbers[spaceNumber] ?? customSeatNumbers[String(spaceNumber)];
    const actualSeatNumber =
      customVal != null ? Number(customVal) : defaultSeatNumber;

    result.push({
      spaceNumber,
      actualSeatNumber,
      defaultSeatNumber,
      isCustomSeatNumber: customVal != null,
      isSpacer,
      jurorInfo: assignment.get(spaceNumber) || null,
    });
  }

  return result;
}

function buildScorecard(jurors, mode = 'jury-box') {
  const discardedField =
    mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';

  let causeCount = 0;
  let hardshipCount = 0;
  let prosecutionPeremptCount = 0;
  let defensePeremptCount = 0;

  for (const juror of jurors) {
    if (juror[discardedField]) {
      if (juror.discardedReason === 'cause') {
        causeCount++;
      }
      if (juror.discardedReason === 'hardship') {
        hardshipCount++;
      }
      if (juror.discardedReason === 'peremptory') {
        if (juror.discarder === 'prosecutor') {
          prosecutionPeremptCount++;
        }
        if (juror.discarder === 'defense') {
          defensePeremptCount++;
        }
        if (juror.discarder === 'both') {
          prosecutionPeremptCount += 0.5;
          defensePeremptCount += 0.5;
        }
      }
    }
  }

  return {
    causeCount,
    hardshipCount,
    prosecutionPeremptCount,
    defensePeremptCount,
  };
}

/**
 * Base hook: returns all jurors for a case.
 *
 * When a CaseDataProvider is present in the tree (i.e. inside the Case page),
 * this hook reads directly from context — the provider's single live query is
 * the only Dexie subscriber for jurors, so a write triggers one read+decrypt
 * pass instead of one per hook instance.  Outside a provider the hook falls
 * back to its own live query (e.g. admin pages, tests).
 */
export function useJurors(caseId) {
  const ctx = useCaseDataContext();
  const { db } = useDatabase();
  const hasContext = ctx?.caseId === caseId;

  // Only subscribe to Dexie when there is no context provider above us.
  // When hasContext is true the query function never touches db.jurors so
  // Dexie does not register a subscription for this hook instance.
  const liveJurors = useLiveQuery(
    async () => {
      if (hasContext) return undefined;
      return fetchJurorsForCase(db, caseId);
    },
    [db, caseId, hasContext],
    undefined,
  );

  const jurors = hasContext ? (ctx.jurors ?? []) : (liveJurors ?? []);

  return {
    jurors,
    isLoading: !hasContext && liveJurors === undefined,
  };
}

/**
 * Hook to get a single juror by ID with live updates.
 * Always uses its own live query — it reads one record, not all of them.
 */
export function useJuror(jurorId) {
  const { db } = useDatabase();

  const juror = useLiveQuery(
    async () => {
      if (!db || !jurorId) return null;
      return db.jurors.get(jurorId);
    },
    [db, jurorId],
    undefined,
  );

  return {
    juror: juror || null,
    isLoading: juror === undefined,
  };
}

/**
 * Returns a seatNumber → juror map for quick seat lookups.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useJurorsBySeat(caseId) {
  const { jurors, isLoading } = useJurors(caseId);

  const seatMap = useMemo(() => {
    const map = {};
    for (const juror of jurors) {
      if (juror.seatNumber != null && juror.seatNumber > 0 && !juror.discarded) {
        map[juror.seatNumber] = juror;
      }
    }
    return map;
  }, [jurors]);

  return { seatMap, isLoading };
}

/**
 * Returns an alternateSeatNumber → juror map.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useAlternateJurorsBySeat(caseId) {
  const { jurors, isLoading } = useJurors(caseId);

  const alternateSeatMap = useMemo(() => {
    const map = {};
    for (const juror of jurors) {
      if (
        juror.alternateSeatNumber != null &&
        juror.alternateSeatNumber > 0 &&
        !juror.alternateDiscarded
      ) {
        map[juror.alternateSeatNumber] = juror;
      }
    }
    return map;
  }, [jurors]);

  return { alternateSeatMap, isLoading };
}

/**
 * Returns jurors in the pool (not seated, not discarded).
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function usePoolJurors(caseId, sortConfig = {}) {
  const { jurors, isLoading } = useJurors(caseId);

  const poolJurors = useMemo(
    () => filterPoolJurors(jurors, sortConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jurors, sortConfig.field, sortConfig.direction],
  );

  return { poolJurors, isLoading };
}

/**
 * Returns dismissed jurors.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useDismissedJurors(caseId, mode = 'jury-box') {
  const { jurors, isLoading } = useJurors(caseId);

  const dismissedJurors = useMemo(
    () => filterDismissedJurors(jurors, mode),
    [jurors, mode],
  );

  return { dismissedJurors, isLoading };
}

/**
 * Returns jurors for a specific row in the jury box, handling spacers.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useJurorsForRow(
  caseId,
  rowNum,
  numCols,
  spacers = [],
  mode = 'jury-box',
  customSeatNumbers = {},
  size = 0,
) {
  const { jurors, isLoading } = useJurors(caseId);
  const spacersKey = JSON.stringify(spacers);
  const customSeatsKey = JSON.stringify(customSeatNumbers);

  const rowJurors = useMemo(
    () => buildRowJurors(jurors, rowNum, numCols, spacers, mode, customSeatNumbers, size),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jurors, rowNum, numCols, spacersKey, mode, customSeatsKey, size],
  );

  return { rowJurors, isLoading };
}

/**
 * Returns strike/cause/hardship counts.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useScorecardData(caseId, mode = 'jury-box') {
  const { jurors, isLoading } = useJurors(caseId);

  const scorecard = useMemo(
    () => (jurors.length ? buildScorecard(jurors, mode) : EMPTY_SCORECARD),
    [jurors, mode],
  );

  return { scorecard, isLoading };
}

/**
 * Searches jurors by name, comments, or demographics.
 * Derived from useJurors so no additional Dexie subscription is needed.
 */
export function useJurorSearch(caseId, searchTerm) {
  const { jurors, isLoading } = useJurors(caseId);

  const results = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase();
    return jurors.filter((j) => {
      if (j.nameAndId?.toLowerCase().includes(term)) return true;
      if (j.comments?.toLowerCase().includes(term)) return true;
      if (j.demographics?.some((d) => d.value?.toLowerCase().includes(term))) {
        return true;
      }
      return false;
    });
  }, [jurors, searchTerm]);

  return { results, isLoading };
}

export default {
  useJurors,
  useJuror,
  useJurorsBySeat,
  useAlternateJurorsBySeat,
  usePoolJurors,
  useDismissedJurors,
  useJurorsForRow,
  useScorecardData,
  useJurorSearch,
};
